import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from './lib/cn'

/** crypto.randomUUID is unavailable in insecure contexts; fall back to a counter. */
let toastSeq = 0
function nextToastId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  toastSeq += 1
  return `toast-${toastSeq}`
}

/* ==========================================================================
 * Toasts
 * ======================================================================== */

export type ToastTone = 'info' | 'success' | 'warning' | 'danger'

export interface ToastOptions {
  title: string
  description?: string
  tone?: ToastTone
  /** Milliseconds before auto-dismiss. Pass 0 to require manual dismissal. */
  duration?: number
  /** Optional inline action, e.g. "View". */
  action?: { label: string; onClick: () => void }
}

interface ToastRecord extends Required<Pick<ToastOptions, 'title' | 'tone' | 'duration'>> {
  id: string
  description?: string
  action?: ToastOptions['action']
}

interface ToastApi {
  toast: (options: ToastOptions) => string
  success: (title: string, description?: string) => string
  error: (title: string, description?: string) => string
  info: (title: string, description?: string) => string
  warning: (title: string, description?: string) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const MAX_VISIBLE = 5

const TONE_ICON: Record<ToastTone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
}

const TONE_ACCENT: Record<ToastTone, string> = {
  info: 'text-info-text',
  success: 'text-success-text',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
}

/**
 * Mount once per app. Renders the stack and provides the `useToast()` API.
 * Toasts land bottom-right on desktop and bottom-centre on mobile, and older
 * ones are dropped once the stack exceeds five so the corner never becomes a
 * wall of messages.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextToastId()
      const record: ToastRecord = {
        id,
        title: options.title,
        description: options.description,
        tone: options.tone ?? 'info',
        duration: options.duration ?? 4500,
        action: options.action,
      }

      setToasts((current) => [...current, record].slice(-MAX_VISIBLE))

      if (record.duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), record.duration)
        )
      }

      return id
    },
    [dismiss]
  )

  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ title, description, tone: 'success' }),
      error: (title, description) => toast({ title, description, tone: 'danger', duration: 7000 }),
      info: (title, description) => toast({ title, description, tone: 'info' }),
      warning: (title, description) => toast({ title, description, tone: 'warning' }),
    }),
    [toast, dismiss]
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[]
  onDismiss: (id: string) => void
}) {
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      // aria-live so screen readers announce without stealing focus.
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
    >
      {toasts.map((toast) => {
        const Icon = TONE_ICON[toast.tone]

        return (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-card border border-line bg-raised p-3.5 shadow-overlay',
              'animate-in slide-in-from-bottom-2 fade-in duration-200'
            )}
          >
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', TONE_ACCENT[toast.tone])} />
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-medium text-foreground">{toast.title}</p>
              {toast.description ? (
                <p className="text-caption text-muted">{toast.description}</p>
              ) : null}
              {toast.action ? (
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick()
                    onDismiss(toast.id)
                  }}
                  className="mt-1 text-caption font-medium text-accent underline-offset-2 hover:underline"
                >
                  {toast.action.label}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => onDismiss(toast.id)}
              className="rounded p-1 text-subtle transition-colors hover:bg-sunken hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )
      })}
    </div>,
    document.body
  )
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a <ToastProvider>')
  return context
}

/* ==========================================================================
 * Progress
 * ======================================================================== */

export interface ProgressBarProps {
  /** Omit for an indeterminate bar. */
  value?: number
  max?: number
  label?: string
  className?: string
}

export function ProgressBar({ value, max = 100, label, className }: ProgressBarProps) {
  const indeterminate = value === undefined
  const pct = indeterminate ? 0 : Math.min(100, (value / max) * 100)

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={indeterminate ? undefined : Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('relative h-1.5 w-full overflow-hidden rounded-full bg-sunken', className)}
    >
      {indeterminate ? (
        <div className="absolute inset-y-0 animate-indeterminate rounded-full bg-accent" />
      ) : (
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      )}
    </div>
  )
}

/* ==========================================================================
 * Skeletons
 * ======================================================================== */

export function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded-lg bg-sunken', className)}
      {...props}
    />
  )
}

export interface SkeletonTextProps {
  lines?: number
  className?: string
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className="h-3.5"
          // Last line short, as real paragraphs are.
          style={index === lines - 1 ? { width: '60%' } : undefined}
        />
      ))}
    </div>
  )
}

export interface SkeletonTableProps {
  rows?: number
  columns?: number
  className?: string
}

export function SkeletonTable({ rows = 6, columns = 4, className }: SkeletonTableProps) {
  return (
    <div className={cn('overflow-hidden rounded-card border border-line bg-surface', className)} aria-hidden>
      <div className="flex gap-4 border-b border-line bg-sunken/60 px-4 py-3">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 border-b border-line px-4 py-3.5 last:border-0">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Page-level loading state that mirrors the real layout — heading, stat row,
 * then content. Matching the shape means the page does not jump when data
 * arrives.
 */
export interface PageSkeletonProps {
  stats?: number
  className?: string
}

export function PageSkeleton({ stats = 4, className }: PageSkeletonProps) {
  return (
    <div className={cn('space-y-6', className)} aria-busy>
      <div className="space-y-2">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80" />
      </div>

      {stats > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: stats }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-card" />
          ))}
        </div>
      ) : null}

      <SkeletonTable />
    </div>
  )
}

/* ==========================================================================
 * ErrorBoundary
 * ======================================================================== */

interface ErrorBoundaryProps {
  children: React.ReactNode
  /** Rendered instead of the default panel. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode
  /** Report to Sentry or similar. */
  onError?: (error: Error, info: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Catches render errors so one broken panel does not blank the whole app.
 * Displays a trace ID when the API supplied one, since that is what support
 * asks for.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info)
    if (import.meta.env.DEV) console.error('Unhandled render error:', error, info)
  }

  private reset = () => this.setState({ error: null })

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4 rounded-card border border-line bg-surface p-6 text-center shadow-card">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-danger-soft">
            <AlertTriangle className="h-5 w-5 text-danger-text" />
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-foreground">Something went wrong</h2>
            <p className="text-caption text-muted">
              This panel failed to render. The rest of the app is unaffected.
            </p>
          </div>
          <p className="break-words rounded-lg bg-sunken px-3 py-2 text-left font-mono text-caption text-muted">
            {error.message}
          </p>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="h-9 rounded-control bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="h-9 rounded-control border border-line-strong px-4 text-sm font-medium text-foreground transition-colors hover:bg-sunken"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    )
  }
}
